import React, { useEffect, useMemo, useState } from "react";
import { Button, Group, Loader, MultiSelect, Stack, Text } from "@mantine/core";
import { useClientGroups } from "../../hooks/useClientGroups";
import { ClientGroup } from "../../types/clientGroups";

interface Props {
  /** Currently selected client-group UUIDs. */
  value: string[];
  /** Updates the parent's selection with the new group-UUID list. */
  onChange: (nextValue: string[]) => void;
  disabled?: boolean;
  label?: string;
  description?: string;
}

/**
 * Reusable Mantine MultiSelect for targeting a marketing campaign at one or
 * more client groups. Fetches the location's active groups on mount and maps
 * them to `{ value: group.id, label: "Name (N)" }`. Selected groups render as
 * removable pills and the input stays searchable so operators can keep adding.
 *
 * The value prop is the list of group UUIDs; the parent posts them as
 * `client_group_ids` and the backend resolves membership live at send time.
 * Render this only when the `client_groups` feature is enabled (the caller
 * gates on `useFeatureAccess().hasFeature("client_groups")`).
 */
export default function ClientGroupsMultiSelect({
  value,
  onChange,
  disabled = false,
  label = "Target Client Groups",
  description = "Members are resolved when the campaign sends, so dynamic groups stay current. Manage groups under Marketing → Client Groups.",
}: Props) {
  const { listGroups } = useClientGroups();

  const [groups, setGroups] = useState<ClientGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumping this re-runs the fetch effect — used by the error-state Retry.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetch = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await listGroups();
        if (!cancelled) {
          setGroups(Array.isArray(response) ? response : []);
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error("Failed to load client groups:", err);
          setError("Failed to load client groups");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetch();

    return () => {
      cancelled = true;
    };
  }, [listGroups, reloadKey]);

  const options = useMemo(
    () =>
      groups.map((group) => ({
        value: group.id,
        label:
          group.member_count != null
            ? `${group.name} (${group.member_count})`
            : group.name,
      })),
    [groups],
  );

  if (isLoading) {
    return (
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          {label}
        </Text>
        <Loader size="xs" />
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          {label}
        </Text>
        <Group gap="sm" align="center">
          <Text size="xs" c="red">
            {error}
          </Text>
          <Button
            size="compact-xs"
            variant="subtle"
            color="var(--brand-teal)"
            onClick={() => setReloadKey((k) => k + 1)}
          >
            Retry
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <MultiSelect
      label={label}
      description={description}
      placeholder={value.length === 0 ? "Select client groups" : undefined}
      data={options}
      value={value}
      onChange={onChange}
      searchable
      clearable
      disabled={disabled}
      nothingFoundMessage="No client groups found"
    />
  );
}
